import {
  ref,
  set,
  onValue,
  onDisconnect,
  serverTimestamp,
  get,
  update,
  runTransaction
} from 'firebase/database';
import firebaseConfig from '../../config';

const { databaseId } = firebaseConfig;

// Presence timeout threshold (30 seconds)
// Users are considered offline if their lastUpdate is older than this
const PRESENCE_TIMEOUT = 30000; // 30 seconds in milliseconds

// Helper function to get current date key in YYYY-MM-DD format
const getCurrentDateKey = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

// Helper function to calculate total session time from session history
// This makes sessionHistory the single source of truth
const calculateTotalFromHistory = (sessionHistory) => {
  if (!sessionHistory || typeof sessionHistory !== 'object') return 0;

  return Object.values(sessionHistory).reduce(
    (total, dayData) => total + (dayData.totalSeconds || 0),
    0
  );
};

function PresenceFactory(firebase) {
  const setUserOnline = async ({
    sessionId,
    uid,
    name,
    isNewVisit = false,
    existingTotalVisits
  }) => {
    const userRef = ref(firebase.rtdb, `userPresence/${sessionId}/${uid}`);

    // Read from database to get sessionHistory and visit count
    let existingVisitCount;
    let existingSessionHistory;
    let existingData;

    if (existingTotalVisits !== undefined) {
      // Use provided visit count (avoid database read to prevent race conditions)
      existingVisitCount = existingTotalVisits;
      // Still need to read sessionHistory from database
      existingData = await get(userRef);
      existingSessionHistory = existingData.exists()
        ? existingData.val().sessionHistory || {}
        : {};
    } else {
      // Read from database for fresh users
      existingData = await get(userRef);
      existingVisitCount = existingData.exists()
        ? existingData.val().totalVisits || 0
        : 0;
      existingSessionHistory = existingData.exists()
        ? existingData.val().sessionHistory || {}
        : {};
    }

    // Calculate new visit count
    const newVisitCount = isNewVisit
      ? existingVisitCount + 1
      : existingVisitCount;

    // Initialize or update today's session history entry
    const dateKey = getCurrentDateKey();
    if (isNewVisit) {
      if (!existingSessionHistory[dateKey]) {
        existingSessionHistory[dateKey] = {
          lastActive: serverTimestamp(),
          totalSeconds: 0,
          visitCount: 1
        };
      } else {
        existingSessionHistory[dateKey].lastActive = serverTimestamp();
        existingSessionHistory[dateKey].visitCount += 1;
      }
    }

    // Calculate totalSessionTime from sessionHistory (single source of truth)
    const calculatedTotal = calculateTotalFromHistory(existingSessionHistory);

    // Set user online, preserving existing data and updating visit info
    await set(userRef, {
      currentVisitStart: isNewVisit
        ? serverTimestamp()
        : existingData && existingData.exists()
          ? existingData.val().currentVisitStart || serverTimestamp()
          : serverTimestamp(),
      isOnline: true,
      lastUpdate: serverTimestamp(),
      name,
      sessionHistory: existingSessionHistory,
      sessionStart: serverTimestamp(),
      totalSessionTime: calculatedTotal,
      totalVisits: newVisitCount
    });

    // Automatically mark user offline when disconnected, preserving session data
    onDisconnect(userRef).update({
      isOnline: false,
      lastUpdate: serverTimestamp()
    });

    return userRef;
  };

  const updateSessionTime = async ({
    sessionId,
    uid,
    sessionTimeDelta = 0
  }) => {
    const userRef = ref(firebase.rtdb, `userPresence/${sessionId}/${uid}`);
    const dateKey = getCurrentDateKey();

    // Use transaction to atomically update sessionHistory and recalculate totalSessionTime
    // This ensures sessionHistory is the single source of truth
    await runTransaction(userRef, (currentData) => {
      if (!currentData) {
        // Initialize new user presence entry
        return {
          isOnline: true,
          lastUpdate: serverTimestamp(),
          sessionHistory: {
            [dateKey]: {
              totalSeconds: sessionTimeDelta,
              lastActive: serverTimestamp(),
              visitCount: 1
            }
          },
          totalSessionTime: sessionTimeDelta
        };
      }

      // Update existing user presence
      const sessionHistory = currentData.sessionHistory || {};

      // Update today's bucket
      if (!sessionHistory[dateKey]) {
        sessionHistory[dateKey] = {
          totalSeconds: sessionTimeDelta,
          lastActive: serverTimestamp(),
          visitCount: 1
        };
      } else {
        sessionHistory[dateKey] = {
          ...sessionHistory[dateKey],
          totalSeconds:
            (sessionHistory[dateKey].totalSeconds || 0) + sessionTimeDelta,
          lastActive: serverTimestamp()
        };
      }

      // Calculate totalSessionTime from sessionHistory (single source of truth)
      const calculatedTotal = calculateTotalFromHistory(sessionHistory);

      return {
        ...currentData,
        isOnline: true,
        lastUpdate: serverTimestamp(),
        sessionHistory,
        totalSessionTime: calculatedTotal
      };
    });

    // Re-establish disconnect handling after update
    onDisconnect(userRef).update({
      isOnline: false,
      lastUpdate: serverTimestamp()
    });
  };

  const setUserOffline = async ({ sessionId, uid }) => {
    const userRef = ref(firebase.rtdb, `userPresence/${sessionId}/${uid}`);

    // Cancel any pending onDisconnect operations
    onDisconnect(userRef).cancel();

    // Read current sessionHistory and recalculate totalSessionTime
    const snapshot = await get(userRef);
    const sessionHistory = snapshot.exists()
      ? snapshot.val().sessionHistory || {}
      : {};

    const calculatedTotal = calculateTotalFromHistory(sessionHistory);

    // Update with calculated session time but preserve the entry
    await update(userRef, {
      isOnline: false,
      totalSessionTime: calculatedTotal,
      lastUpdate: serverTimestamp()
    });
  };

  const subscribeToSessionPresence = ({ sessionId, callback }) => {
    const presenceRef = ref(firebase.rtdb, `userPresence/${sessionId}`);

    return onValue(presenceRef, (snapshot) => {
      const presenceData = snapshot.val();
      const onlineUsers = [];
      const now = Date.now();

      if (presenceData) {
        Object.entries(presenceData).forEach(([uid, userData]) => {
          if (userData && userData.isOnline) {
            // Check if lastUpdate is recent (within PRESENCE_TIMEOUT)
            const lastUpdate = userData.lastUpdate || 0;
            const timeSinceUpdate = now - lastUpdate;

            // Only include users who have updated within the timeout threshold
            if (timeSinceUpdate <= PRESENCE_TIMEOUT) {
              onlineUsers.push({
                id: uid,
                ...userData
              });
            }
          }
        });
      }

      callback(onlineUsers);
    });
  };

  const monitorConnection = (callback) => {
    const connectedRef = ref(firebase.rtdb, '.info/connected');
    return onValue(connectedRef, (snapshot) => {
      const isConnected = snapshot.val();
      callback(isConnected);
    });
  };

  const getUserPresence = async ({ sessionId, uid }) => {
    const userRef = ref(firebase.rtdb, `userPresence/${sessionId}/${uid}`);
    const snapshot = await get(userRef);
    return snapshot.val();
  };

  return {
    setUserOnline,
    updateSessionTime,
    setUserOffline,
    subscribeToSessionPresence,
    monitorConnection,
    getUserPresence
  };
}

export default PresenceFactory;
